import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../context/AdminContext';
import axios from 'axios';
import { Shield, CheckCircle, XCircle, Trash2, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const { isAdmin, logout } = useAdmin();
  const navigate = useNavigate();
  const [pendingAnimals, setPendingAnimals] = useState([]);
  const [pendingVaccines, setPendingVaccines] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/admin-login');
      return;
    }
    fetchPendingData();
  }, [isAdmin, navigate]);

  const fetchPendingData = async () => {
    try {
      const [animalsRes, vaccinesRes] = await Promise.all([
        axios.get(`${API}/animals`),
        axios.get(`${API}/vaccines/all`).catch(() => ({ data: [] }))
      ]);
      
      setPendingAnimals(animalsRes.data.filter(a => !a.approved));
      
      const allVaccines = [];
      for (const animal of animalsRes.data) {
        const vaxRes = await axios.get(`${API}/vaccines/${animal.id}`);
        allVaccines.push(...vaxRes.data.map(v => ({ ...v, animalName: animal.name })));
      }
      setPendingVaccines(allVaccines.filter(v => !v.approved));
      
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const approveAnimal = async (id) => {
    try {
      await axios.put(`${API}/animals/${id}`, { approved: true });
      fetchPendingData();
    } catch (error) {
      console.error('Error approving animal:', error);
    }
  };

  const rejectAnimal = async (id) => {
    if (window.confirm('Are you sure you want to reject and delete this animal?')) {
      try {
        await axios.delete(`${API}/animals/${id}`);
        fetchPendingData();
      } catch (error) {
        console.error('Error rejecting animal:', error);
      }
    }
  };

  const approveVaccine = async (id) => {
    try {
      await axios.put(`${API}/vaccines/${id}`, { approved: true });
      fetchPendingData();
    } catch (error) {
      console.error('Error approving vaccine:', error);
    }
  };

  const rejectVaccine = async (id) => {
    if (window.confirm('Are you sure you want to reject and delete this vaccine record?')) {
      try {
        await axios.delete(`${API}/vaccines/${id}`);
        fetchPendingData();
      } catch (error) {
        console.error('Error rejecting vaccine:', error);
      }
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-gradient-to-br from-purple-500 to-indigo-600 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <div className="bg-white p-3 rounded-xl shadow-lg">
                <Shield className="w-8 h-8 text-purple-600" />
              </div>
              <div>
                <h1 className="text-4xl font-extrabold text-white font-heading" data-testid="admin-panel-title">
                  Admin Panel
                </h1>
                <p className="text-white/90 font-medium">Manage approvals and data</p>
              </div>
            </div>
            <button
              onClick={() => { logout(); navigate('/'); }}
              className="bg-white text-purple-600 px-6 py-3 rounded-full font-bold shadow-xl hover:shadow-2xl transition-all"
              data-testid="logout-button"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div className="bg-white p-6 rounded-2xl border border-orange-100 shadow-sm">
            <div className="text-3xl font-extrabold text-orange-600 mb-1">{pendingAnimals.length}</div>
            <div className="text-sm font-bold text-text-secondary">Pending Animals</div>
          </div>
          <div className="bg-white p-6 rounded-2xl border border-orange-100 shadow-sm">
            <div className="text-3xl font-extrabold text-blue-600 mb-1">{pendingVaccines.length}</div>
            <div className="text-sm font-bold text-text-secondary">Pending Vaccines</div>
          </div>
        </div>

        <div className="space-y-8">
          <div>
            <h2 className="text-2xl font-bold text-text-primary mb-4 font-heading">Pending Animal Approvals</h2>
            {pendingAnimals.length === 0 ? (
              <div className="bg-white p-8 rounded-2xl text-center text-text-muted" data-testid="no-pending-animals">
                No pending animals
              </div>
            ) : (
              <div className="space-y-4">
                {pendingAnimals.map((animal) => (
                  <motion.div
                    key={animal.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-2xl shadow-sm border border-orange-100 p-6"
                    data-testid={`pending-animal-${animal.id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-text-primary mb-2">{animal.name}</h3>
                        <div className="flex flex-wrap gap-2 text-sm">
                          <span className="bg-orange-100 px-3 py-1 rounded-full font-medium">{animal.species}</span>
                          <span className="bg-orange-100 px-3 py-1 rounded-full font-medium">{animal.gender}</span>
                          <span className="bg-orange-100 px-3 py-1 rounded-full font-medium">{animal.area}</span>
                          {animal.ownerName && (
                            <span className="bg-blue-100 px-3 py-1 rounded-full font-medium">Pet - {animal.ownerName}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => approveAnimal(animal.id)}
                          className="p-3 rounded-xl bg-green-100 text-green-700 hover:bg-green-200 transition-all"
                          data-testid={`approve-animal-${animal.id}`}
                        >
                          <CheckCircle className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => rejectAnimal(animal.id)}
                          className="p-3 rounded-xl bg-red-100 text-red-700 hover:bg-red-200 transition-all"
                          data-testid={`reject-animal-${animal.id}`}
                        >
                          <XCircle className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          <div>
            <h2 className="text-2xl font-bold text-text-primary mb-4 font-heading">Pending Vaccine Approvals</h2>
            {pendingVaccines.length === 0 ? (
              <div className="bg-white p-8 rounded-2xl text-center text-text-muted" data-testid="no-pending-vaccines">
                No pending vaccines
              </div>
            ) : (
              <div className="space-y-4">
                {pendingVaccines.map((vaccine) => (
                  <motion.div
                    key={vaccine.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-2xl shadow-sm border border-blue-100 p-6"
                    data-testid={`pending-vaccine-${vaccine.id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-text-primary mb-2">{vaccine.vaccineName}</h3>
                        <div className="text-sm text-text-secondary space-y-1">
                          <div>Animal: <span className="font-bold">{vaccine.animalName}</span></div>
                          <div>Date Given: {vaccine.dateGiven} | Valid Till: {vaccine.validTill}</div>
                          <div>Vet: {vaccine.vet}</div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => approveVaccine(vaccine.id)}
                          className="p-3 rounded-xl bg-green-100 text-green-700 hover:bg-green-200 transition-all"
                          data-testid={`approve-vaccine-${vaccine.id}`}
                        >
                          <CheckCircle className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => rejectVaccine(vaccine.id)}
                          className="p-3 rounded-xl bg-red-100 text-red-700 hover:bg-red-200 transition-all"
                          data-testid={`reject-vaccine-${vaccine.id}`}
                        >
                          <XCircle className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
